import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

interface ProfileContextType {
    hasProfileData: boolean;
    setHasProfileData: (value: boolean) => void;
    isLoading: boolean;
}

const ProfileContext = createContext<ProfileContextType | null>(null);

export const useProfile = () => {
    const context = useContext(ProfileContext);
    if (!context) {
        throw new Error('useProfile must be used within a ProfileProvider');
    }
    return context;
};

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [hasProfileData, setHasProfileData] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { token, user } = useAuth();

    useEffect(() => {
        const checkProfileData = async () => {
            if (!token || !user) {
                setHasProfileData(false);
                setIsLoading(false);
                return;
            }

            try {
                // Check for resume data
                const resumeResponse = await axios.get('/api/resume', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const hasResume = resumeResponse.data && resumeResponse.data.length > 0;

                // Check for education data
                const educationResponse = await axios.get('/api/education', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const hasEducation = educationResponse.data && educationResponse.data.length > 0;

                // Check for employment data
                const employmentResponse = await axios.get('/api/employment', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const hasEmployment = employmentResponse.data && employmentResponse.data.length > 0;

                const profileSectionsResponse = await axios.get('/api/profile-section', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const profileSections = profileSectionsResponse.data || [];
                const sectionContent = await Promise.all(
                    profileSections.map(async (section: { id: string }) => {
                        const [detailsResponse, skillsResponse] = await Promise.all([
                            axios.get(`/api/profile-section/${section.id}/detail`, {
                                headers: { Authorization: `Bearer ${token}` }
                            }),
                            axios.get(`/api/profile-section/${section.id}/skill`, {
                                headers: { Authorization: `Bearer ${token}` }
                            })
                        ]);
                        return (detailsResponse.data?.length || 0) > 0 || (skillsResponse.data?.length || 0) > 0;
                    })
                );
                const hasProfileSections = sectionContent.some(Boolean);

                // User has profile data if they have at least one content source configured
                setHasProfileData(hasResume || hasEducation || hasEmployment || hasProfileSections);
            } catch (error) {
                console.error('Error checking profile data:', error);
                setHasProfileData(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkProfileData();
    }, [token, user]);

    const value = {
        hasProfileData,
        setHasProfileData,
        isLoading
    };

    return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}; 
